import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { formatDateTime, formatStatus } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  ClipboardList, 
  Search, 
  Filter,
  User,
  Clock,
  RefreshCw,
  Eye,
  Calendar,
  Loader2,
  FileText,
  ChevronRight,
  Shield,
  AlertTriangle,
  CheckCircle2,
  Edit3,
  Trash2,
  LogIn,
  LogOut,
  Mail,
  ArrowRight
} from 'lucide-react';

const ACTION_COLORS = {
  CREATE: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
  UPDATE: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  DELETE: 'bg-red-500/20 text-red-400 border-red-500/50',
  STATUS_CHANGE: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  LOGIN: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
  LOGOUT: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50',
  EMAIL_SENT: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
};

const ACTION_ICONS = {
  CREATE: <CheckCircle2 className="w-4 h-4" />,
  UPDATE: <Edit3 className="w-4 h-4" />,
  DELETE: <Trash2 className="w-4 h-4" />,
  STATUS_CHANGE: <ArrowRight className="w-4 h-4" />,
  LOGIN: <LogIn className="w-4 h-4" />,
  LOGOUT: <LogOut className="w-4 h-4" />,
  EMAIL_SENT: <Mail className="w-4 h-4" />,
};

const ENTITY_TYPES = [
  'incident',
  'incident_evidence',
  'shift',
  'patrol_run',
  'patrol_route',
  'user',
  'client',
  'site',
  'checkpoint'
];

const ACTIONS = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'STATUS_CHANGE',
  'LOGIN',
  'LOGOUT',
  'EMAIL_SENT'
];

export default function AuditLogPage() {
  const { api, isAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [skip, setSkip] = useState(0);
  const limit = 50;

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/audit?skip=${skip}&limit=${limit}`;
      
      if (entityTypeFilter && entityTypeFilter !== 'all') {
        url += `&entity_type=${entityTypeFilter}`;
      }
      if (actionFilter && actionFilter !== 'all') {
        url += `&action=${actionFilter}`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      if (dateFrom) {
        url += `&date_from=${new Date(dateFrom).toISOString()}`;
      }
      if (dateTo) {
        url += `&date_to=${new Date(dateTo + 'T23:59:59').toISOString()}`;
      }

      const response = await api.get(url);
      setLogs(response.data.items || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, [api, skip, entityTypeFilter, actionFilter, searchTerm, dateFrom, dateTo]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/audit/stats?days=7');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching audit stats:', error);
    }
  }, [api]);

  useEffect(() => {
    if (isAdmin) {
      fetchLogs();
      fetchStats();
    }
  }, [fetchLogs, fetchStats, isAdmin]);

  const handleSearch = () => {
    setSkip(0);
    fetchLogs();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setEntityTypeFilter('all');
    setActionFilter('all');
    setDateFrom('');
    setDateTo('');
    setSkip(0);
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
          <h2 className="text-xl font-rajdhani font-bold text-white mb-2">Access Denied</h2>
          <p className="text-zinc-500">Audit logs are only accessible to administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white tracking-wide">
            AUDIT LOG
          </h1>
          <p className="text-sm text-zinc-500">{totalCount} total events</p>
        </div>
        <Button
          onClick={() => {
            setSkip(0);
            fetchLogs();
            fetchStats();
          }}
          variant="outline"
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          data-testid="refresh-audit-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          REFRESH
        </Button>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <ClipboardList className="w-5 h-5 text-blue-400" />
                <span className="text-2xl font-mono font-bold text-zinc-300">
                  {stats.total_events}
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-2">Events (7 days)</p>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <LogIn className="w-5 h-5 text-purple-400" />
                <span className="text-2xl font-mono font-bold text-zinc-300">
                  {stats.logins}
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-2">Logins</p>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <span className="text-2xl font-mono font-bold text-zinc-300">
                  {stats.by_action?.CREATE || 0}
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-2">Created</p>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-[#3f3f46]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Edit3 className="w-5 h-5 text-yellow-400" />
                <span className="text-2xl font-mono font-bold text-zinc-300">
                  {(stats.by_action?.UPDATE || 0) + (stats.by_action?.STATUS_CHANGE || 0)}
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-2">Updates</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search by user email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 bg-[#09090b] border-[#3f3f46] text-white"
                data-testid="audit-search-input"
              />
            </div>
            <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
              <SelectTrigger className="bg-[#09090b] border-[#3f3f46]" data-testid="entity-type-filter">
                <Filter className="w-4 h-4 mr-2 text-zinc-500" />
                <SelectValue placeholder="Entity Type" />
              </SelectTrigger>
              <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                <SelectItem value="all">All Entities</SelectItem>
                {ENTITY_TYPES.map(type => (
                  <SelectItem key={type} value={type}>{formatStatus(type)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="bg-[#09090b] border-[#3f3f46]" data-testid="action-filter">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                <SelectItem value="all">All Actions</SelectItem>
                {ACTIONS.map(action => (
                  <SelectItem key={action} value={action}>{formatStatus(action)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-[#09090b] border-[#3f3f46] text-white"
              placeholder="From Date"
              data-testid="date-from-filter"
            />
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-[#09090b] border-[#3f3f46] text-white"
              placeholder="To Date"
              data-testid="date-to-filter"
            />
          </div>
          <div className="flex gap-2 mt-4">
            <Button 
              onClick={handleSearch}
              className="bg-blue-500 hover:bg-blue-600"
              data-testid="apply-filters-btn"
            >
              Apply Filters
            </Button>
            <Button 
              onClick={handleClearFilters}
              variant="outline"
              className="border-zinc-600"
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Audit Log List */}
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardHeader className="border-b border-[#3f3f46] bg-[#18181b]/50 py-3 px-4">
          <CardTitle className="text-sm font-rajdhani font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-blue-400" />
            AUDIT TRAIL
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
            </div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-zinc-500">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No audit logs found</p>
            </div>
          ) : (
            <div className="divide-y divide-[#3f3f46]">
              {logs.map((log) => (
                <div 
                  key={log.id}
                  className="p-4 hover:bg-[#27272a]/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedLog(log)}
                  data-testid={`audit-log-${log.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${ACTION_COLORS[log.action] || 'bg-zinc-500/20'}`}>
                        {ACTION_ICONS[log.action] || <FileText className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge className={`${ACTION_COLORS[log.action] || 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50'} text-xs px-1.5 py-0 rounded-sm border`}>
                            {formatStatus(log.action)}
                          </Badge>
                          <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-400">
                            {formatStatus(log.entity_type)}
                          </Badge>
                        </div>
                        <p className="text-sm text-zinc-200 truncate">
                          <span className="text-zinc-500">by</span> {log.actor_email}
                        </p>
                        <div className="flex items-center gap-4 mt-1 text-xs text-zinc-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDateTime(log.timestamp)}
                          </span>
                          {log.ip_address && (
                            <span className="font-mono">{log.ip_address}</span>
                          )}
                        </div>
                        {log.notes && (
                          <p className="text-xs text-zinc-400 mt-1 truncate italic">"{log.notes}"</p>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-zinc-600 flex-shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
        
        {/* Pagination */}
        {totalCount > limit && (
          <div className="p-4 border-t border-[#3f3f46] flex items-center justify-between">
            <p className="text-sm text-zinc-500">
              Showing {skip + 1} - {Math.min(skip + limit, totalCount)} of {totalCount}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSkip(Math.max(0, skip - limit))}
                disabled={skip === 0}
                className="border-zinc-600"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSkip(skip + limit)}
                disabled={skip + limit >= totalCount}
                className="border-zinc-600"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Log Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          {selectedLog && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={`${ACTION_COLORS[selectedLog.action]} text-xs px-2 py-0.5 rounded-sm border`}>
                    {formatStatus(selectedLog.action)}
                  </Badge>
                  <Badge variant="outline" className="text-xs border-zinc-600 text-zinc-400">
                    {formatStatus(selectedLog.entity_type)}
                  </Badge>
                </div>
                <DialogTitle className="font-rajdhani text-xl">Audit Log Details</DialogTitle>
              </DialogHeader>
              
              <ScrollArea className="flex-1">
                <div className="space-y-4 p-1">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-3">
                      <div className="p-2 bg-[#09090b] rounded border border-[#3f3f46]">
                        <span className="text-zinc-500 text-xs block mb-1">Actor</span>
                        <span className="text-zinc-200">{selectedLog.actor_email}</span>
                      </div>
                      <div className="p-2 bg-[#09090b] rounded border border-[#3f3f46]">
                        <span className="text-zinc-500 text-xs block mb-1">Role</span>
                        <span className="text-zinc-200">{formatStatus(selectedLog.actor_role)}</span>
                      </div>
                      <div className="p-2 bg-[#09090b] rounded border border-[#3f3f46]">
                        <span className="text-zinc-500 text-xs block mb-1">Timestamp</span>
                        <span className="text-zinc-200 font-mono text-xs">{formatDateTime(selectedLog.timestamp)}</span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="p-2 bg-[#09090b] rounded border border-[#3f3f46]">
                        <span className="text-zinc-500 text-xs block mb-1">Entity ID</span>
                        <span className="text-zinc-200 font-mono text-xs truncate block">{selectedLog.entity_id}</span>
                      </div>
                      <div className="p-2 bg-[#09090b] rounded border border-[#3f3f46]">
                        <span className="text-zinc-500 text-xs block mb-1">IP Address</span>
                        <span className="text-zinc-200 font-mono">{selectedLog.ip_address || '-'}</span>
                      </div>
                      <div className="p-2 bg-[#09090b] rounded border border-[#3f3f46]">
                        <span className="text-zinc-500 text-xs block mb-1">User Agent</span>
                        <span className="text-zinc-200 text-xs truncate block">{selectedLog.user_agent || '-'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  {selectedLog.notes && (
                    <div className="p-3 bg-[#09090b] rounded border border-[#3f3f46]">
                      <span className="text-zinc-500 text-xs block mb-1">Notes</span>
                      <span className="text-zinc-200 text-sm">{selectedLog.notes}</span>
                    </div>
                  )}

                  {/* Before Snapshot */}
                  {selectedLog.before_snapshot && Object.keys(selectedLog.before_snapshot).length > 0 && (
                    <div>
                      <h4 className="text-sm font-rajdhani font-semibold text-zinc-400 uppercase tracking-wider mb-2">Before</h4>
                      <pre className="text-xs text-zinc-300 bg-[#09090b] p-3 rounded border border-[#3f3f46] overflow-x-auto">
                        {JSON.stringify(selectedLog.before_snapshot, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* After Snapshot */}
                  {selectedLog.after_snapshot && Object.keys(selectedLog.after_snapshot).length > 0 && (
                    <div>
                      <h4 className="text-sm font-rajdhani font-semibold text-zinc-400 uppercase tracking-wider mb-2">After</h4>
                      <pre className="text-xs text-zinc-300 bg-[#09090b] p-3 rounded border border-[#3f3f46] overflow-x-auto">
                        {JSON.stringify(selectedLog.after_snapshot, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Changes */}
                  {selectedLog.changes && Object.keys(selectedLog.changes).length > 0 && (
                    <div>
                      <h4 className="text-sm font-rajdhani font-semibold text-zinc-400 uppercase tracking-wider mb-2">Changes</h4>
                      <pre className="text-xs text-zinc-300 bg-[#09090b] p-3 rounded border border-[#3f3f46] overflow-x-auto">
                        {JSON.stringify(selectedLog.changes, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
